import React from "react";
import { DispatchProp } from "react-redux";
import { isEmpty } from "lodash";
import { ChoiceModel } from "../../models/ChoiceModel";
import { MentionModel } from "../../models/MentionModel";
import { QuestionModel } from "../../models/QuestionModel";
import { MentionBuilderDelegate } from "../../models/builders/MentionBuilder";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ThemeModel } from "../../models/ThemeModel";
import { VariableModel } from "../../models/VariableModel";
import {
    setMultipleVariablesMentions,
    setQuestionFieldStatus,
    setVariableMentions,
} from "../../redux/actions";
import { __getStore__ } from "../../redux/store";
import { QuestionType } from "../../models/enums/QuestionType";
import { isNullOrEmpty } from "../../utils";
import { getDisabledColumnsForRowBasedOnChoiceSelected } from "../../redux/reducers/questionFieldStatusReducer";

export interface ChoiceHandler {
    select(): void;
}

export type ChoiceRef = React.MutableRefObject<ChoiceHandler>;

export interface ChoiceProps {
    // Store state
    question: QuestionModel;
    questionnaire: QuestionnaireModel;
    mentions: MentionModel[];
    theme: ThemeModel;
    language: string;
    matrix: number;
    // Props
    choice: ChoiceModel;
    choiceRef: ChoiceRef;
    variable: VariableModel;
    gridRendering?: boolean;
    openEnd?: string;
    isFirst?: boolean;
    isLast?: boolean;
    horizontal?: boolean;
    label?: string;
    isDisabled?: boolean;
    isSelected?: boolean;
    executeUpdateForIdAfterSelection?: string;
}

export abstract class BaseChoice<P, S> extends React.Component<P & ChoiceProps & DispatchProp, S> {
    setMentions(variableName: string, builder: MentionBuilderDelegate, matrixIndex?: number) {
        const { matrix, dispatch } = this.props;
        const index = matrixIndex || matrix;
        dispatch(setVariableMentions(variableName, builder, index));
    }

    getMention = (mentionIndex?: number) => {
        const { mentions } = this.props;
        return mentions[mentionIndex || 0];
    };

    getMentionValues = () => {
        const { mentions } = this.props;
        return mentions.map((m) => m.value);
    };

    getMentionValue = (mentionIndex?: number) =>
        this.getMention(mentionIndex) === undefined ? "" : this.getMention(mentionIndex).value;

    getMentionOpenEnd = (mentionIndex?: number) =>
        this.getMention(mentionIndex) === undefined ? "" : this.getMention(mentionIndex).openEnd;

    getLatestMentionsForVariableName = (variableName: string) => {
        return __getStore__().getState().interview.variables.mentionsByVariableName[variableName];
    };

    updateMentionsAfterDisablingFields = (variableNames: any) => {
        const { dispatch } = this.props;
        dispatch(setMultipleVariablesMentions(variableNames));
    };

    updateQuestionFieldDisabledStatus = (ids: any, disabled: boolean) => {
        const { dispatch } = this.props;
        dispatch(setQuestionFieldStatus(ids, disabled, true));
    };

    getLatestMentionsFromState = () => {
        return __getStore__().getState().interview.variables.mentionsByVariableName;
    };

    disableAndResetHybridQuestionMapppedCell = (currentFieldIdentifier: string) => {
        const { question } = this.props;

        if (
            question?.parent?.type === QuestionType.HybridGrid &&
            !isNullOrEmpty(currentFieldIdentifier)
        ) {
            const [questionId, currentFieldVariableName] = currentFieldIdentifier.split("&");
            const checkAndUpdateStatusForKeys = [];
            const disableColumnsList = [];

            // To reset multiple mentions at once
            let mentionsByVariable: { [variableName: string]: MentionBuilderDelegate } = {};
            const addMention = (variableName: string, builder: MentionBuilderDelegate) => {
                mentionsByVariable = {
                    ...mentionsByVariable,
                    [variableName]: builder,
                };
            };

            const rows = question?.parent?.rows;
            const filteredRows = rows.filter(
                (x) =>
                    x.variables &&
                    x.variables.length > 0 &&
                    x.variables.find((vb) => vb.name === currentFieldVariableName),
            );

            if (filteredRows && filteredRows.length > 0) {
                // Get columns to be disabled for entire row if there is any mention selected for it.
                const latestMentions = this.getLatestMentionsFromState();

                getDisabledColumnsForRowBasedOnChoiceSelected(
                    filteredRows[0],
                    disableColumnsList,
                    latestMentions,
                );

                // Don't add filter for current variablename as index will vary
                filteredRows[0].variables.forEach((loopVariable, index) => {
                    const mappedFieldIdentifier = `${questionId}&${loopVariable.name}`;

                    // Each variable new state to be checked
                    checkAndUpdateStatusForKeys.push(mappedFieldIdentifier);

                    if (disableColumnsList && disableColumnsList.length > 0) {
                        const isDisabled = disableColumnsList
                            .join(",")
                            .replace(/\s/g, "")
                            .split(",")
                            .some((c) => c === index.toString());

                        if (isDisabled && currentFieldIdentifier !== mappedFieldIdentifier) {
                            // Check latest mentions and reset only if there is any
                            const currentMentions = this.getLatestMentionsForVariableName(
                                loopVariable.name,
                            );

                            if (currentMentions && currentMentions.length > 0) {
                                addMention(loopVariable.name, (m) => m.clear());
                            }
                        }
                    }
                });
            }

            if (!isEmpty(mentionsByVariable)) {
                this.updateMentionsAfterDisablingFields(mentionsByVariable);
            }

            // In reducer state updation logic is written
            if (!isEmpty(checkAndUpdateStatusForKeys)) {
                this.updateQuestionFieldDisabledStatus(checkAndUpdateStatusForKeys, false);
            }
        }
    };

    abstract renderChoice(choice: ChoiceModel);

    render() {
        const { choice } = this.props;
        return this.renderChoice(choice);
    }
}
